import { IonIcon } from '@ionic/react';
import { trendingDown, trendingUp } from 'ionicons/icons';
import React from 'react';
import { createUseStyles } from 'react-jss';

const useStyles = createUseStyles({
  '@keyframes fadeIn': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  fadeIn: {
    opacity: 0,
    animation: '$fadeIn 0.5s ease-in-out forwards',
  },
  hidden: {
    opacity: 0,
  },
  card: {
    boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.04)',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    borderRadius: '10px',
    minHeight: '150px',
    minWidth: '150px',
    padding: '15px',
    backgroundColor: 'white',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    transition: 'background-color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      backgroundColor: '#1a1a1a',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.3)',
    },
  },
  icon: {
    position: 'absolute',
    top: '8px',
    left: 0,
    color: (props: PercentChangeStyleProps) => (props.isPositive ? '#3E9D8F' : '#BF4858'),
    transition: 'color 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      color: (props: PercentChangeStyleProps) => (props.isPositive ? '#4CAF50' : '#F44336'),
    },
  },
  primaryText: {
    fontSize: '24px',
    fontWeight: 600,
    color: '#282828',
    margin: 0,
    transition: 'color 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      color: '#ffffff',
    },
  },
  secondaryText: {
    fontSize: '12px',
    fontWeight: 400,
    color: '#666666',
    margin: 0,
    transition: 'color 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      color: '#b3b3b3',
    },
  },
});

interface PercentChangeStyleProps {
  isPositive: boolean;
}

interface PercentChangeProps {
  value: number;
  label: string;
  percentChange: number;
  isLoading: boolean;
}

const PercentChange: React.FC<PercentChangeProps> = ({
  value,
  label,
  percentChange,
  isLoading,
}) => {
  const isPositive = percentChange >= 0;
  const classes = useStyles({ isPositive });

  const contentClass = isLoading ? classes.hidden : classes.fadeIn;

  const thisDateLastMonth = React.useMemo(() => {
    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
    return lastMonth.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  }, []);

  return (
    <div className={classes.card}>
      <div style={{ display: 'flex' }}>
        <div style={{ flexBasis: '40%' }}>
          <div className={classes.icon} style={{ flexBasis: '50%' }}>
            <IonIcon
              icon={isPositive ? trendingUp : trendingDown}
              style={{ width: '70px', height: '40px' }}
              className={contentClass}
            />
          </div>
        </div>
        <div style={{ flexBasis: '60%', textAlign: 'right' }} className={contentClass}>
          <p className={classes.secondaryText}>
            {isPositive ? 'up' : 'down'} {Math.abs(percentChange)}%
          </p>
          <p className={classes.secondaryText}>from {thisDateLastMonth}</p>
        </div>
      </div>
      <div>
        <h2 className={`${classes.primaryText} ${contentClass}`}>{value}</h2>
        <p className={classes.secondaryText}>{label}</p>
      </div>
    </div>
  );
};

export default PercentChange;
